import React, { useEffect, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Tooltip,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Paper,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";

import { Editor } from "@tinymce/tinymce-react";

import axios from "axios";

import {
  startGoogleOAuth,
  accountsNeedingGoogleReauth,
} from "./googleOAuthFlow";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GoogleIcon from "@mui/icons-material/Google";
import MailLockOutlinedIcon from "@mui/icons-material/MailLockOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

/** Acento UNIBOX (alineado con badge e icono nuevo mensaje). */
const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const TINYMCE_SCRIPT = `${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`;
const TINYMCE_BASE = `${process.env.PUBLIC_URL || ""}/tinymce`;

function buildSignatureEditorInit({ height = 240, toolbar, plugins }) {
  return {
    height,
    menubar: false,
    plugins,
    toolbar,
    branding: false,
    promotion: false,
    license_key: "gpl",
    base_url: TINYMCE_BASE,
    suffix: ".min",
    skin: "oxide",
    content_css: "default",
    resize: true,
    statusbar: false,
    content_style:
      "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; line-height: 1.5; color: #1e293b; margin: 12px; }",
  };
}

const MAIN_SIGNATURE_INIT = buildSignatureEditorInit({
  height: 260,
  plugins: "lists link image autolink code",
  toolbar:
    "undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image | removeformat",
});

const REPLY_SIGNATURE_INIT = buildSignatureEditorInit({
  height: 200,
  plugins: "lists link autolink",
  toolbar: "bold italic underline | bullist numlist | link | removeformat",
});

const API = String(process.env.REACT_APP_API_URL || "")
  .trim()
  .replace(/\/+$/, "");

export default function AccountManager({ onAccountsChange, onImapSynced }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = ACCENT;
  const accentHover = ACCENT_HOVER;
  const onAccent = "#0f172a";
  const heroGradient = `linear-gradient(135deg, ${alpha(accent, isDark ? 0.22 : 0.14)} 0%, ${alpha(theme.palette.background.paper, 1)} 52%, ${alpha(theme.palette.background.default, isDark ? 0.95 : 0.55)} 100%)`;

  const dialogPaperProps = {
    sx: {
      borderRadius: 2.5,
      bgcolor: theme.palette.background.paper,
      backgroundImage: "none",
      border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
      boxShadow: isDark
        ? `0 24px 48px -12px ${alpha("#000000", 0.55)}`
        : `0 24px 48px -12px ${alpha(theme.palette.common.black, 0.16)}`,
    },
  };
  const fieldRoundedSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: theme.palette.background.paper,
    },
  };

  const [accounts, setAccounts] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openImap, setOpenImap] = useState(false);
  const [imapBusy, setImapBusy] = useState(false);
  const [imapForm, setImapForm] = useState({
    email: "",
    password: "",
    imapUsername: "",
    imapHost: "mail.dlx.dk",
    imapPort: "993",
    imapSecure: true,
    smtpHost: "mail.dlx.dk",
    smtpPort: "587",
    smtpSecure: false,
  });
  const [editingAccount, setEditingAccount] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [replySignatureHtml, setReplySignatureHtml] = useState("");

  // Mostrar las firma logo
  const [logoPath, setLogoPath] = useState(null);

  const [feedbackNotice, setFeedbackNotice] = useState({
    open: false,
    title: "",
    message: "",
    severity: "success",
  });
  const [orphanStats, setOrphanStats] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanLoadError, setOrphanLoadError] = useState("");

  const closeFeedbackNotice = () =>
    setFeedbackNotice((s) => ({ ...s, open: false }));

  const loadOrphanStats = async () => {
    setOrphanLoadError("");
    try {
      const res = await axios.get(`${API}/emails/maintenance/orphans/count`);
      setOrphanStats(res.data);
    } catch (e) {
      setOrphanStats(null);
      const st = e?.response?.status;
      if (st === 404) {
        setOrphanLoadError(
          "El servidor aún no tiene esta función. Despliega la última versión de unibox-backend."
        );
      } else {
        setOrphanLoadError(
          e?.response?.data?.error ||
            e?.message ||
            "No se pudo comprobar los huérfanos."
        );
      }
    }
  };

  const cleanupOrphans = async () => {
    let stats = orphanStats;
    if (!stats) {
      try {
        const res = await axios.get(`${API}/emails/maintenance/orphans/count`);
        stats = res.data;
        setOrphanStats(stats);
      } catch (e) {
        setFeedbackNotice({
          open: true,
          title: "No se pudo limpiar",
          message:
            e?.response?.data?.error ||
            e?.message ||
            "No se pudo comprobar los huérfanos.",
          severity: "error",
        });
        return;
      }
    }

    const ok = window.confirm(
      `¿Eliminar datos huérfanos de la base de datos?\n\n` +
        `Correos: ${stats?.emails ?? "?"}\n` +
        `Cuerpos: ${stats?.bodies ?? "?"}\n` +
        `Adjuntos: ${stats?.attachments ?? "?"}\n\n` +
        "Solo se borra lo que ya no se ve en MATRIXBOX (cuentas eliminadas)."
    );
    if (!ok) return;
    setOrphanBusy(true);
    try {
      const res = await axios.post(`${API}/emails/maintenance/orphans/cleanup`);
      if (res.data?.queued) {
        setFeedbackNotice({
          open: true,
          title: "Limpieza en curso",
          message:
            res.data?.message ||
            "La limpieza se ejecuta en segundo plano (1-3 min con muchos registros). " +
              "Pulsa «Actualizar contador» cada 20-30 segundos hasta ver ceros.",
          severity: "success",
        });
        const poll = [10000, 25000, 45000, 70000, 100000, 130000];
        poll.forEach((ms) => setTimeout(() => loadOrphanStats(), ms));
        return;
      }
      const d = res.data?.deleted || {};
      setFeedbackNotice({
        open: true,
        title: "Limpieza completada",
        message: `Correos: ${d.emails || 0}\nCuerpos: ${d.bodies || 0}\nAdjuntos: ${d.attachments || 0}`,
        severity: "success",
      });
      await loadOrphanStats();
    } catch (e) {
      setFeedbackNotice({
        open: true,
        title: "No se pudo limpiar",
        message:
          e?.response?.data?.error ||
          e?.message ||
          "Error en la limpieza de huérfanos.",
        severity: "error",
      });
    } finally {
      setOrphanBusy(false);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadOrphanStats();
  }, []);

  const loadAccounts = async () => {
    if (!API) {
      console.error("Falta REACT_APP_API_URL en .env de unibox-mail");
      return;
    }
    const res = await axios.get(`${API}/auth/accounts`);
    setAccounts(res.data);
  };

  const refreshAccountsAndNotifyParent = async () => {
    await loadAccounts();
    onAccountsChange?.();
  };

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "#" + "000000".substring(0, 6 - c.length) + c;
  }

  const openGoogleOAuth = async (
    hintEmail = "",
    { oauthClient = null } = {}
  ) => {
    try {
      await startGoogleOAuth({
        email: hintEmail,
        oauthClient,
      });
    } catch (e) {
      console.error(e);
      setFeedbackNotice({
        open: true,
        title: "Conexión con Google",
        message:
          e?.response?.data?.error ||
          e?.message ||
          "Error al iniciar conexión con Google.",
        severity: "error",
      });
    }
  };

  const reconnectGoogle = (acc) => {
    const email = String(acc?.email || "").toLowerCase();
    const oauthClient = email.endsWith("@manterola.com")
      ? "manterola"
      : "default";
    return openGoogleOAuth(email, { oauthClient });
  };

  const addGoogleAccount = (oauthClient = "default") =>
    openGoogleOAuth("", { oauthClient });

  const [openGooglePick, setOpenGooglePick] = useState(false);

  const openEditDialog = async (acc) => {
    setEditingAccount(acc);

    try {
      const res = await axios.get(`${API}/signatures/${acc.email}`);
      setSignatureHtml(res.data.signature_html || "");
      setReplySignatureHtml(res.data.reply_signature_html || "");
      setLogoPath(
        res.data.logo_path ? `${API}${res.data.logo_path}` : null
      );
    } catch (err) {
      if (err.response?.status === 404) {
        // crear fila vacía automáticamente
        await axios.put(`${API}/signatures/${acc.email}`, {
          signature_html: "",
          reply_signature_html: "",
        });

        setSignatureHtml("");
        setReplySignatureHtml("");
        setLogoPath(null); // <-- mejor poner null
      }
    }
    setOpenEdit(true);
  };

  const saveSignatures = async () => {
    await axios.put(`${API}/signatures/${editingAccount.email}`, {
      signature_html: signatureHtml,
      reply_signature_html: replySignatureHtml,
    });

    setOpenEdit(false);
    await refreshAccountsAndNotifyParent();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await axios.post(
        `${API}/signatures/upload-logo/${editingAccount.email}`,
        form
      );

      setFeedbackNotice({
        open: true,
        title: "Logo guardado",
        message: "El logo se ha subido correctamente.",
        severity: "success",
      });

      if (res.data?.logo_path) {
        const fullUrl = `${API}${res.data.logo_path}`;
        setLogoPath(fullUrl);

        // Insertar la imagen real en TinyMCE
        if (!signatureHtml.includes(fullUrl)) {
          setSignatureHtml(
            signatureHtml +
              `<br/><img src="${fullUrl}" style="max-width:180px;" />`
          );
        }
      }
    } catch (err) {
      console.error(err);
      setFeedbackNotice({
        open: true,
        title: "Error al subir el logo",
        message:
          err?.response?.data?.error ||
          err?.message ||
          "No se pudo subir el archivo. Comprueba el formato y la conexión.",
        severity: "error",
      });
    }
  };

  const deleteAccount = async (acc) => {
    const id = typeof acc === "object" ? acc.id : acc;
    const email = typeof acc === "object" ? acc.email : "";
    const ok = window.confirm(
      `¿Eliminar la cuenta ${email || ""}?\n\n` +
        "• Los correos ya sincronizados dejarán de mostrarse (quedan huérfanos en la base de datos).\n" +
        "• Si solo caducó Google (modo Prueba, ~7 días), usa «Reconectar» en lugar de borrar.\n" +
        "• Al volver a conectar el mismo email se intentará recuperar el historial huérfano."
    );
    if (!ok) return;
    await axios.delete(`${API}/auth/account/${id}`);
    await refreshAccountsAndNotifyParent();
  };

  const applyGmailImapPreset = () => {
    setImapForm((p) => ({
      ...p,
      imapHost: "imap.gmail.com",
      imapPort: "993",
      imapSecure: true,
      smtpHost: "smtp.gmail.com",
      smtpPort: "587",
      smtpSecure: false,
    }));
  };

  const applyDlxPreset = () => {
    setImapForm((p) => ({
      ...p,
      imapHost: "mail.dlx.dk",
      imapPort: "993",
      imapSecure: true,
      smtpHost: "mail.dlx.dk",
      smtpPort: "587",
      smtpSecure: false,
    }));
  };

  const [syncBusyId, setSyncBusyId] = useState(null);

  const syncAccount = async (acc) => {
    if (!API || syncBusyId) return;
    const isImap = acc.account_type === "imap";
    setSyncBusyId(acc.id);
    try {
      const res = await axios.get(`${API}/gmail/sync/${acc.email}`, {
        params: { notify: "1" },
        timeout: 25_000,
      });
      if (res.data?.rateLimited) {
        setFeedbackNotice({
          open: true,
          title: "Gmail en pausa",
          message: `${acc.email}: rate-limit de Google. Espera unos minutos y reintenta solo esta cuenta.`,
          severity: "warning",
        });
        return;
      }
      setFeedbackNotice({
        open: true,
        title: isImap ? "IMAP en curso" : "Sync lanzado",
        message: isImap
          ? `${acc.email}: sincronización iniciada. La bandeja se actualizará sola en unos segundos.`
          : `${acc.email}: sincronización en segundo plano. La bandeja se actualizará sola.`,
        severity: "success",
      });
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "GOOGLE_REAUTH_REQUIRED") {
        setFeedbackNotice({
          open: true,
          title: "Reconectar Google",
          message: `${acc.email}: la autorización caducó. Usa Reconectar en esta cuenta.`,
          severity: "warning",
        });
        return;
      }
      const details =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        err?.message ||
        "Error desconocido";
      setFeedbackNotice({
        open: true,
        title: "Error de sync",
        message: `${acc.email}: ${details}`,
        severity: "error",
      });
    } finally {
      setSyncBusyId(null);
    }
  };

  const submitImap = async () => {
    if (!API) {
      setFeedbackNotice({
        open: true,
        title: "Configuración",
        message:
          "Falta REACT_APP_API_URL en el .env de unibox-mail.\n\nEjemplo:\nREACT_APP_API_URL=http://localhost:4000\n\n(Reinicia npm start tras guardar.)",
        severity: "warning",
      });
      return;
    }
    if (!imapForm.email.trim() || !imapForm.password || !imapForm.imapHost.trim()) {
      setFeedbackNotice({
        open: true,
        title: "Faltan datos",
        message: "Indica email, contraseña y servidor IMAP.",
        severity: "warning",
      });
      return;
    }
    setImapBusy(true);
    const imapUrl = `${API}/auth/imap/connect`;
    try {
      await axios.post(imapUrl, {
        email: imapForm.email.trim(),
        password: imapForm.password,
        imapUsername: imapForm.imapUsername.trim() || imapForm.email.trim(),
        imapHost: imapForm.imapHost.trim(),
        imapPort: Number(imapForm.imapPort) || 993,
        imapSecure: !!imapForm.imapSecure,
        smtpHost: imapForm.smtpHost.trim() || imapForm.imapHost.trim(),
        smtpPort: Number(imapForm.smtpPort) || 587,
        smtpSecure: !!imapForm.smtpSecure,
      });
      setOpenImap(false);
      setImapForm((p) => ({ ...p, password: "" }));
      await refreshAccountsAndNotifyParent();
      setFeedbackNotice({
        open: true,
        title: "Cuenta IMAP guardada",
        message:
          "Usa el icono Sync de esta cuenta (o Sync en el menú) para traer los correos del servidor.",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      const st = e?.response?.status;
      if (st === 404) {
        setFeedbackNotice({
          open: true,
          title: "No se encontró el servicio (404)",
          message:
            `El backend no expone POST /auth/imap/connect en:\n${imapUrl}\n\n` +
            `• REACT_APP_API_URL debe ser la URL del unibox-backend (suele ser puerto 4000), no la del propio React.\n` +
            `• Ejecuta npm run build en unibox-backend y reinicia npm start (o usa npm run dev).\n` +
            `• Comprueba en la terminal del backend que arranca sin errores.`,
          severity: "error",
        });
        return;
      }
      const d = e?.response?.data;
      const msg = [d?.error, d?.hint, d?.details].filter(Boolean).join("\n\n");
      setFeedbackNotice({
        open: true,
        title: "No se pudo guardar la cuenta IMAP",
        message:
          msg ||
          e?.message ||
          "Revisa la consola de red y los logs del backend.",
        severity: "error",
      });
    } finally {
      setImapBusy(false);
    }
  };

  const accentBtnSx = {
    bgcolor: accent,
    color: onAccent,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 2,
    boxShadow: "none",
    "&:hover": { bgcolor: accentHover, boxShadow: "none" },
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: theme.palette.background.default,
        px: { xs: 2, sm: 3 },
        py: { xs: 2.5, sm: 3 },
      }}
    >
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
            background: heroGradient,
            p: { xs: 2.5, sm: 3 },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            flexWrap="wrap"
            useFlexGap
            sx={{ gap: 2 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(accent, isDark ? 0.28 : 0.22),
                color: theme.palette.text.primary,
              }}
            >
              <ManageAccountsOutlinedIcon fontSize="medium" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                UNIBOX
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  color: "text.primary",
                  mt: 0.25,
                }}
              >
                Cuentas de correo
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75, maxWidth: 520, lineHeight: 1.55 }}
              >
                Conecta Gmail (elige Stage o Manterola) o un buzón IMAP.
                Ship-quik / WFT pueden seguir en IMAP.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            useFlexGap
            sx={{ gap: 1.5, mt: 2.75 }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              sx={{
                ...accentBtnSx,
                flex: { sm: 1 },
                py: 1.1,
              }}
              onClick={() => setOpenGooglePick(true)}
            >
              Añadir cuenta Google
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<MailLockOutlinedIcon />}
              onClick={() => setOpenImap(true)}
              sx={{
                flex: { sm: 1 },
                py: 1.1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, isDark ? 0.5 : 0.35),
                color: "text.primary",
                "&:hover": {
                  borderColor: accent,
                  bgcolor: alpha(accent, isDark ? 0.12 : 0.06),
                },
              }}
            >
              IMAP
            </Button>
          </Stack>
        </Paper>

        <Typography
          variant="subtitle2"
          sx={{
            mb: 1.5,
            fontWeight: 700,
            letterSpacing: 0.2,
            color: "text.primary",
          }}
        >
          Cuentas conectadas ({accounts.length})
        </Typography>

        {accounts.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              borderStyle: "dashed",
              borderColor: alpha(theme.palette.divider, 0.9),
              bgcolor: alpha(theme.palette.background.paper, 0.7),
            }}
          >
            <EmailOutlinedIcon
              sx={{ fontSize: 44, color: "action.disabled", mb: 1.5 }}
            />
            <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
              No hay cuentas conectadas. Usa los botones de arriba para añadir
              Google o IMAP.
            </Typography>
          </Paper>
        ) : (
          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {accounts.map((acc) => {
              const isImap = acc.account_type === "imap";
              const needsReconnect =
                !isImap &&
                (acc.auth_status === "reauth_required" ||
                  acc.auth_status === "missing_tokens");
              return (
                <Paper
                  key={acc.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    borderColor: alpha(theme.palette.divider, 0.85),
                    bgcolor: theme.palette.background.paper,
                    transition: theme.transitions.create(
                      ["box-shadow", "border-color"],
                      { duration: theme.transitions.duration.shortest }
                    ),
                    "&:hover": {
                      borderColor: alpha(accent, 0.55),
                      boxShadow: `0 8px 28px ${alpha(accent, isDark ? 0.18 : 0.1)}`,
                    },
                  }}
                >
                  <ListItem
                    sx={{ py: 1.75, px: 2, alignItems: "flex-start" }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.75} sx={{ pr: 0.5 }}>
                        <Tooltip
                          title={
                            isImap
                              ? "Sincronizar buzón IMAP ahora"
                              : "Sincronizar esta cuenta Google ahora"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => syncAccount(acc)}
                            disabled={syncBusyId === acc.id}
                            sx={{
                              border: `1px solid ${alpha(accent, 0.45)}`,
                              borderRadius: 2,
                              color: accent,
                              "&:hover": {
                                bgcolor: alpha(accent, 0.08),
                              },
                            }}
                          >
                            {syncBusyId === acc.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <SyncOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {isImap &&
                        String(acc.email || "")
                          .toLowerCase()
                          .endsWith("@manterola.com") ? (
                          <Tooltip title="Pasar a Google OAuth (recomendado)">
                            <IconButton
                              size="small"
                              onClick={() => reconnectGoogle(acc)}
                              sx={{
                                border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
                                borderRadius: 2,
                                color: theme.palette.success.dark,
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.success.main, 0.08),
                                },
                              }}
                            >
                              <GoogleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {!isImap ? (
                          <Tooltip
                            title={
                              needsReconnect
                                ? "Google caducó (modo Prueba). Reconectar sin borrar."
                                : "Reconectar Google (renovar tokens)"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => reconnectGoogle(acc)}
                              sx={{
                                border: `1px solid ${alpha(
                                  needsReconnect
                                    ? theme.palette.warning.main
                                    : accent,
                                  0.45
                                )}`,
                                borderRadius: 2,
                                color: needsReconnect
                                  ? theme.palette.warning.dark
                                  : accent,
                                "&:hover": {
                                  bgcolor: alpha(
                                    needsReconnect
                                      ? theme.palette.warning.main
                                      : accent,
                                    0.08
                                  ),
                                },
                              }}
                            >
                              <LockResetOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Editar firmas">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(acc)}
                            sx={{
                              border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
                              borderRadius: 2,
                              color: theme.palette.success.main,
                              "&:hover": {
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar cuenta">
                          <IconButton
                            size="small"
                            onClick={() => deleteAccount(acc)}
                            sx={{
                              border: `1px solid ${alpha(theme.palette.error.main, 0.45)}`,
                              borderRadius: 2,
                              color: theme.palette.error.main,
                              "&:hover": {
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemAvatar sx={{ minWidth: 56, mt: 0.25 }}>
                      <Avatar
                        sx={{
                          bgcolor: stringToColor(acc.email),
                          width: 44,
                          height: 44,
                          fontSize: 16,
                          fontWeight: 700,
                          boxShadow: `0 2px 8px ${alpha(stringToColor(acc.email), 0.35)}`,
                        }}
                      >
                        {acc.email[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ gap: 1, rowGap: 0.5 }}
                        >
                          <Typography
                            component="span"
                            variant="body1"
                            sx={{ fontWeight: 700, wordBreak: "break-all" }}
                          >
                            {acc.email}
                          </Typography>
                          <Chip
                            size="small"
                            icon={
                              isImap ? (
                                <MailLockOutlinedIcon sx={{ "&&": { fontSize: 16 } }} />
                              ) : (
                                <GoogleIcon sx={{ "&&": { fontSize: 16 } }} />
                              )
                            }
                            label={isImap ? "IMAP" : "Google"}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              bgcolor: isImap
                                ? alpha(theme.palette.action.hover, 0.6)
                                : alpha(accent, isDark ? 0.28 : 0.2),
                              color: "text.primary",
                              "& .MuiChip-icon": { color: "inherit" },
                            }}
                          />
                          {isImap &&
                          String(acc.email || "")
                            .toLowerCase()
                            .endsWith("@manterola.com") ? (
                            <Chip
                              size="small"
                              icon={
                                <GoogleIcon sx={{ "&&": { fontSize: 16 } }} />
                              }
                              label="Pasar a Google"
                              color="success"
                              variant="outlined"
                              onClick={() => reconnectGoogle(acc)}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                cursor: "pointer",
                              }}
                            />
                          ) : null}
                          {needsReconnect ? (
                            <Chip
                              size="small"
                              icon={
                                <WarningAmberOutlinedIcon
                                  sx={{ "&&": { fontSize: 16 } }}
                                />
                              }
                              label="Reconectar"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 24, fontWeight: 700, fontSize: "0.7rem" }}
                            />
                          ) : null}
                        </Stack>
                      }
                      secondary={
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5, lineHeight: 1.5 }}
                        >
                          ID {acc.id}
                          {!isImap &&
                            ` · Historial ${acc.last_history_id ?? "—"}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                </Paper>
              );
            })}
          </List>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 2,
          borderStyle: "dashed",
          borderColor: alpha(theme.palette.warning.main, 0.35),
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
          Mantenimiento de base de datos
        </Typography>
        {orphanStats ? (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.5 }}>
            Huérfanos (cuentas eliminadas): {orphanStats.emails} correos,{" "}
            {orphanStats.bodies} cuerpos, {orphanStats.attachments} adjuntos.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.5 }}>
            {orphanLoadError ||
              "Comprueba cuántos registros huérfanos hay antes de limpiar."}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            disabled={orphanBusy}
            onClick={loadOrphanStats}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Actualizar contador
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            disabled={orphanBusy}
            onClick={cleanupOrphans}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {orphanBusy ? "Limpiando…" : "Eliminar huérfanos"}
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={openGooglePick}
        onClose={() => setOpenGooglePick(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Conectar Google</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elige el cliente OAuth según la empresa. Luego selecciona la cuenta
            en Google.
          </Typography>
          <Stack spacing={1.25}>
            <Button
              variant="contained"
              startIcon={<GoogleIcon />}
              sx={{ ...accentBtnSx, justifyContent: "flex-start", py: 1.25 }}
              onClick={() => {
                setOpenGooglePick(false);
                addGoogleAccount("default");
              }}
            >
              Stage Design (@stagedesign.es)
            </Button>
            <Button
              variant="outlined"
              startIcon={<GoogleIcon />}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
              }}
              onClick={() => {
                setOpenGooglePick(false);
                addGoogleAccount("manterola");
              }}
            >
              Manterola (@manterola.com)
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenGooglePick(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImap}
        onClose={() => !imapBusy && setOpenImap(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          ...dialogPaperProps,
          sx: {
            ...dialogPaperProps.sx,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
              background: heroGradient,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: alpha(accent, isDark ? 0.3 : 0.25),
                  color: theme.palette.text.primary,
                }}
              >
                <MailLockOutlinedIcon fontSize="medium" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.7,
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  Servidor propio
                </Typography>
                <Typography
                  component="div"
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.35,
                    mt: 0.35,
                  }}
                >
                  Conectar buzón IMAP
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, lineHeight: 1.55 }}
                >
                  Configura recepción (IMAP) y envío (SMTP). Ej.:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    mail.dlx.dk
                  </Box>
                  — puertos habituales IMAP 143/993 y SMTP 587/465.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 2.5,
              mt: 1,
              borderRadius: 2,
              alignItems: "flex-start",
              bgcolor: alpha(accent, isDark ? 0.1 : 0.06),
              borderColor: alpha(accent, isDark ? 0.4 : 0.28),
              "& .MuiAlert-icon": { color: accent },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              Para <strong>mail.dlx.dk</strong> (DuelHost): IMAP puerto{" "}
              <strong>993</strong> con SSL directo. SMTP suele ir en{" "}
              <strong>587</strong> (STARTTLS) o <strong>465</strong> (SSL).
            </Typography>
          </Alert>

          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 2.5, borderRadius: 2 }}
            action={
              <Stack direction="row" spacing={0.75}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={applyDlxPreset}
                  sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                >
                  Plantilla DLX
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={applyGmailImapPreset}
                  sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                >
                  Plantilla Gmail
                </Button>
              </Stack>
            }
          >
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              <strong>No uses IMAP para Gmail/Google Workspace</strong> (p.ej.{" "}
              <code>imap.gmail.com</code>): Google corta el ancho de banda y el
              buzón queda bloqueado. Para esas cuentas usa{" "}
              <strong>Conectar Gmail (OAuth)</strong>. IMAP queda para servidores
              propios (DLX, cPanel, etc.).
            </Typography>
          </Alert>

          <Stack spacing={2.25}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, 0.85),
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 18,
                    borderRadius: 1,
                    bgcolor: accent,
                    flexShrink: 0,
                  }}
                />
                <KeyOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Credenciales
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  value={imapForm.email}
                  onChange={(e) =>
                    setImapForm((p) => ({ ...p, email: e.target.value }))
                  }
                  fullWidth
                  required
                  autoComplete="username"
                  sx={fieldRoundedSx}
                />
                <TextField
                  label="Contraseña"
                  type="password"
                  value={imapForm.password}
                  onChange={(e) =>
                    setImapForm((p) => ({ ...p, password: e.target.value }))
                  }
                  fullWidth
                  required
                  autoComplete="current-password"
                  sx={fieldRoundedSx}
                />
                <TextField
                  label="Usuario IMAP (si distinto del email)"
                  value={imapForm.imapUsername}
                  onChange={(e) =>
                    setImapForm((p) => ({ ...p, imapUsername: e.target.value }))
                  }
                  fullWidth
                  sx={fieldRoundedSx}
                />
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, 0.85),
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 18,
                    borderRadius: 1,
                    bgcolor: accent,
                    flexShrink: 0,
                  }}
                />
                <MoveToInboxOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Entrada — IMAP
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Servidor IMAP"
                  value={imapForm.imapHost}
                  onChange={(e) =>
                    setImapForm((p) => ({ ...p, imapHost: e.target.value }))
                  }
                  fullWidth
                  placeholder="mail.dlx.dk"
                  sx={fieldRoundedSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Puerto IMAP"
                    value={imapForm.imapPort}
                    onChange={(e) =>
                      setImapForm((p) => ({ ...p, imapPort: e.target.value }))
                    }
                    fullWidth
                    sx={{ ...fieldRoundedSx, flex: 1 }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      minHeight: { sm: 56 },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={imapForm.imapSecure}
                          onChange={(e) =>
                            setImapForm((p) => ({
                              ...p,
                              imapSecure: e.target.checked,
                            }))
                          }
                          sx={{
                            color: "text.secondary",
                            "&.Mui-checked": { color: accent },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          SSL/TLS directo
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            ej. puerto 993
                          </Typography>
                        </Typography>
                      }
                      sx={{ ml: 0, alignItems: "flex-start" }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, 0.85),
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 18,
                    borderRadius: 1,
                    bgcolor: accent,
                    flexShrink: 0,
                  }}
                />
                <SendOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Salida — SMTP
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Servidor SMTP"
                  value={imapForm.smtpHost}
                  onChange={(e) =>
                    setImapForm((p) => ({ ...p, smtpHost: e.target.value }))
                  }
                  fullWidth
                  placeholder="mail.dlx.dk"
                  sx={fieldRoundedSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Puerto SMTP"
                    value={imapForm.smtpPort}
                    onChange={(e) =>
                      setImapForm((p) => ({ ...p, smtpPort: e.target.value }))
                    }
                    fullWidth
                    sx={{ ...fieldRoundedSx, flex: 1 }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      minHeight: { sm: 56 },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={imapForm.smtpSecure}
                          onChange={(e) =>
                            setImapForm((p) => ({
                              ...p,
                              smtpSecure: e.target.checked,
                            }))
                          }
                          sx={{
                            color: "text.secondary",
                            "&.Mui-checked": { color: accent },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          SSL directo (SMTP)
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            ej. puerto 465
                          </Typography>
                        </Typography>
                      }
                      sx={{ ml: 0, alignItems: "flex-start" }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            bgcolor: alpha(theme.palette.background.default, 0.65),
          }}
        >
          <Button
            onClick={() => setOpenImap(false)}
            disabled={imapBusy}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submitImap}
            disabled={imapBusy}
            startIcon={<MailLockOutlinedIcon />}
            sx={accentBtnSx}
          >
            {imapBusy ? "Guardando…" : "Guardar y conectar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackNotice.open}
        onClose={closeFeedbackNotice}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          {feedbackNotice.title}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={
              feedbackNotice.severity === "success"
                ? "success"
                : feedbackNotice.severity === "warning"
                  ? "warning"
                  : "error"
            }
            variant="outlined"
            sx={{
              alignItems: "flex-start",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Typography
              component="div"
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {feedbackNotice.message}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={closeFeedbackNotice} sx={accentBtnSx}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOGO DE EDICIÓN DE FIRMAS */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          ...dialogPaperProps,
          sx: { ...dialogPaperProps.sx, overflow: "hidden" },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
              background: heroGradient,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: alpha(accent, isDark ? 0.3 : 0.25),
                  color: theme.palette.text.primary,
                }}
              >
                <EditOutlinedIcon fontSize="medium" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.7,
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  Identidad del correo
                </Typography>
                <Typography
                  component="div"
                  variant="h6"
                  sx={{ fontWeight: 800, letterSpacing: -0.35, mt: 0.35 }}
                >
                  Firmas de la cuenta
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.75, lineHeight: 1.55 }}
                  noWrap
                >
                  {editingAccount?.email}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 2.5,
              borderRadius: 2,
              alignItems: "flex-start",
              bgcolor: alpha(accent, isDark ? 0.1 : 0.06),
              borderColor: alpha(accent, isDark ? 0.4 : 0.28),
              "& .MuiAlert-icon": { color: accent },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              La <strong>firma estándar</strong> se añade al final de los mensajes
              nuevos. La de <strong>respuestas</strong> aparece arriba cuando contestas.
            </Typography>
          </Alert>

          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: alpha(theme.palette.divider, isDark ? 0.35 : 0.18),
                bgcolor: alpha(theme.palette.background.default, isDark ? 0.35 : 0.45),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    Firma estándar
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mensajes nuevos y reenvíos
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<UploadFileOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: alpha(accent, 0.45),
                    color: accent,
                    "&:hover": {
                      borderColor: accent,
                      bgcolor: alpha(accent, 0.08),
                    },
                  }}
                >
                  Subir logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleLogoUpload(e)}
                  />
                </Button>
              </Stack>

              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.2)}`,
                  bgcolor: theme.palette.background.paper,
                  "& .tox-tinymce": { border: "none !important" },
                  "& .tox-editor-header": {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)} !important`,
                  },
                }}
              >
                <Editor
                  tinymceScriptSrc={TINYMCE_SCRIPT}
                  value={signatureHtml}
                  init={MAIN_SIGNATURE_INIT}
                  onEditorChange={(content) => setSignatureHtml(content)}
                />
              </Box>

              {logoPath ? (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px dashed ${alpha(accent, 0.35)}`,
                    bgcolor: alpha(accent, isDark ? 0.08 : 0.04),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 1 }}
                  >
                    Logo en firma
                  </Typography>
                  <img
                    src={logoPath}
                    alt="Logo de firma"
                    style={{
                      maxWidth: "160px",
                      maxHeight: "72px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
              ) : null}
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: alpha(theme.palette.divider, isDark ? 0.35 : 0.18),
                bgcolor: alpha(theme.palette.background.default, isDark ? 0.35 : 0.45),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <ReplyOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    Firma para respuestas
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Se muestra antes del hilo al contestar
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.2)}`,
                  bgcolor: theme.palette.background.paper,
                  "& .tox-tinymce": { border: "none !important" },
                  "& .tox-editor-header": {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)} !important`,
                  },
                }}
              >
                <Editor
                  tinymceScriptSrc={TINYMCE_SCRIPT}
                  value={replySignatureHtml}
                  init={REPLY_SIGNATURE_INIT}
                  onEditorChange={(content) => setReplySignatureHtml(content)}
                />
              </Box>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
            bgcolor: alpha(theme.palette.background.default, isDark ? 0.4 : 0.5),
          }}
        >
          <Button
            onClick={() => setOpenEdit(false)}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button variant="contained" sx={accentBtnSx} onClick={saveSignatures}>
            Guardar firmas
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
